/**
 * Product image gallery: a horizontally scroll-snapped track with dot
 * navigation. Dots both scroll-to (click) and reflect-scroll-position
 * (IntersectionObserver on each slide) rather than only doing one or the
 * other, so it stays correct whether the visitor clicks a dot or swipes.
 *
 * Also listens for `variant:update` (dispatched by furlouve-product-buy.js)
 * so selecting a variant with its own featured_media scrolls the gallery
 * to the matching slide, keyed by the same data-media-id the theme's
 * native product-media snippet uses to match variant.featured_media.id.
 */
import { ThemeEvents } from '@theme/events';

const gallery = document.querySelector('[data-flv-gallery]');
const track = gallery?.querySelector('[data-flv-gallery-track]');
const slides = gallery ? [...gallery.querySelectorAll('[data-flv-gallery-slide]')] : [];
const dots = gallery ? [...gallery.querySelectorAll('[data-flv-gallery-dot]')] : [];

if (gallery && track && slides.length > 1) {
  /** @param {number} index */
  const goToSlide = (index) => {
    slides[index]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      goToSlide(Number(/** @type {HTMLElement} */ (dot).dataset.index ?? 0));
    });
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = slides.indexOf(entry.target);
        dots.forEach((dot, dotIndex) => dot.classList.toggle('flv-product__gallery-dot--active', dotIndex === index));
      });
    },
    { root: track, threshold: 0.6 }
  );
  slides.forEach((slide) => observer.observe(slide));

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const mediaId = /** @type {CustomEvent} */ (event).detail?.resource?.featured_media?.id;
    if (mediaId == null) return;

    const index = slides.findIndex(
      (slide) => Number(/** @type {HTMLElement} */ (slide).dataset.mediaId) === mediaId
    );
    if (index === -1) return;

    goToSlide(index);
  });
}
